import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import {
  Autocomplete,
  AutocompleteChangeReason,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { clone, isEmpty, isEqual } from "lodash";
import SnackbarComponent from "../UI/SnackbarComponent/SnackbarComponent";
import "./EntitySelector.scss";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAgentData from "../../core/hooks/data/useAgentData";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";
import theme from "../../muiTheme";

const ENTITY_OPTIONS_PAGE_SIZE = 50;

const EntitySelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, startTransition] = useTransition();

  const fromSearchParam = useMemo(
    () =>
      searchParams.get("from")?.trim() ===
      process.env.REACT_APP_ENTITY_SELECTOR_PAGE
        ? null
        : searchParams.get("from"),
    [searchParams]
  );

  const {
    selectedEntityList: savedSelectedEntities,
    setSelectedEntityList,
    entityOptions,
    fetchEntityOptions,
    loadingState,
    getCupsForAgent,
  } = useAgentData();

  const { setSnackbarData, snackbarData } = useContext(TotalEnergiesContext);

  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const filteredOptions = useMemo(() => {
    return entityOptions
      .filter((opt) =>
        opt.toLowerCase().includes(searchInputValue.toLowerCase())
      )
      .slice(0, ENTITY_OPTIONS_PAGE_SIZE);
  }, [entityOptions, searchInputValue]);

  useEffect(() => {
    if (!isEqual(selectedValues, savedSelectedEntities)) {
      setSelectedValues(
        clone(savedSelectedEntities).filter((cif) =>
          entityOptions.includes(cif)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSelectedEntities, entityOptions]);

  useEffect(() => {
    const abortController = new AbortController();
    if (!loadingState.entityOptions) {
      fetchEntityOptions(abortController.signal);
    }

    return () => {
      abortController.abort();
    };
  }, []);

  const handleOnChange = useCallback(
    (_: any, value: string[], reason: AutocompleteChangeReason) => {
      startTransition(() => {
        if (selectedValues.length >= 10 && reason === "selectOption") {
          setSnackbarData({
            text: t("Entity Selector.Snackbar Warning"),
            isSuccessSeverity: false,
            isOpen: true,
          });
        } else {
          setSelectedValues(value);
        }
      });
    },
    [selectedValues.length, setSnackbarData, t]
  );

  const handleOnEnter = useCallback(() => {
    setSelectedEntityList(selectedValues);
    getCupsForAgent(selectedValues).then((responseList) => {
      if (
        !responseList ||
        responseList.some(
          (resp) =>
            resp.status === "rejected" ||
            ("value" in resp && resp.value?.emptyCupList === true)
        )
      ) {
        setSnackbarData({
          text: t("Entity Selector.Empty Cups Response"),
          isOpen: true,
          isSuccessSeverity: false,
        });
      } else {
        const toPath = isEmpty(fromSearchParam)
          ? process.env.REACT_APP_BASE_URL!
          : fromSearchParam!;
        navigate(toPath);
      }
    });
  }, [
    fromSearchParam,
    getCupsForAgent,
    navigate,
    selectedValues,
    setSelectedEntityList,
    setSnackbarData,
    t,
  ]);

  const isLoading = useMemo(
    () => Object.values(loadingState).some((state) => state),
    [loadingState]
  );

  return (
    <Container
      maxWidth="md"
      className="entity-selector"
      sx={{
        py: 10,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
      }}
    >
      {isLoading && (
        <LoadingComponentComponent
          backgroundColor={theme.palette.common.black}
          opacity={0.4}
          isLoading={true}
          size={60}
        />
      )}
      <Typography variant="h3">{t("Entity Selector.Title")}</Typography>
      <Box width="100%" minHeight="32px" display="flex" flexWrap="wrap" gap={1}>
        {selectedValues.map((option, index) => (
          <Chip
            className="entity-chip"
            key={index}
            label={option}
            onDelete={() => {
              const newValue = selectedValues.filter((_, i) => i !== index);
              setSelectedValues(newValue);
            }}
          />
        ))}
        {!isEmpty(selectedValues) && (
          <Button variant="text" onClick={() => setSelectedValues([])}>
            {t("Tablas.Borrar todo")}
          </Button>
        )}
      </Box>
      <Autocomplete
        multiple
        autoComplete={false}
        disableCloseOnSelect
        onInputChange={(_, newInputValue) => {
          startTransition(() => {
            setSearchInputValue(newInputValue);
          });
        }}
        options={filteredOptions}
        value={selectedValues}
        noOptionsText={t("Tablas.No hay coincidencias")}
        limitTags={0}
        onChange={handleOnChange}
        renderTags={() => null}
        style={{ width: "100%" }}
        renderOption={(props, option, { selected }) => (
          <ListItem {...props}>
            <Checkbox
              icon={<span className="t-icon-checkbox-default" />}
              checkedIcon={<span className="t-icon t-icon-checkbox-checked" />}
              sx={{ fontSize: "24px" }}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option}
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField
            sx={{
              width: "100%",
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                boxShadow: "none",
              },
            }}
            InputProps={{
              ...params.InputProps,
              fullWidth: true,
              endAdornment: (
                <Typography
                  component="span"
                  className="t-custom-dropdown t-icon-arrow-down"
                  data-testid="dropdown-arrow"
                  sx={{
                    color: "primary.main",
                    position: "absolute",
                    right: "12px",
                    pointerEvents: "none",
                  }}
                />
              ),
            }}
            placeholder={
              isEmpty(selectedValues)
                ? "CIF"
                : t("Entity Selector.Selected number", {
                    count: selectedValues.length,
                  })
            }
          />
        )}
      />
      <ButtonElement
        styles={{ marginTop: "2rem" }}
        disabled={isEmpty(selectedValues)}
        onClick={handleOnEnter}
      >
        {t("Entity Selector.Enter Button")}
      </ButtonElement>
      <SnackbarComponent
        snackbarData={snackbarData}
        setSnackbarData={setSnackbarData}
        autoHideDuration={8000}
      />
    </Container>
  );
};

export default EntitySelector;
